import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, CalendarOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { listHolidays, addHoliday, removeHoliday, errMsg } from "@/lib/api";
import { ymd } from "@/lib/hours";

const pretty = (d) =>
  new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

export const HolidayManager = () => {
  const qc = useQueryClient();
  const today = ymd(new Date());
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["holidays"], queryFn: async () => (await listHolidays()).data });
  const upcoming = (data || []).filter((h) => h.date >= today);

  const add = async () => {
    if (!date) return toast.error("Pick a date");
    if (date < today) return toast.error("Pick today or a future date");
    setBusy(true);
    try {
      await addHoliday({ date, note: note.trim() });
      await qc.invalidateQueries({ queryKey: ["holidays"] });
      toast.success(`Closed on ${pretty(date)}`);
      setDate("");
      setNote("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (h) => {
    try {
      await removeHoliday(h.id);
      await qc.invalidateQueries({ queryKey: ["holidays"] });
      toast.success("Holiday removed");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-[180px_1fr_auto] gap-3 items-end">
        <div className="space-y-1.5">
          <Label>Date</Label>
          <Input type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} data-testid="holiday-date-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Reason (optional)</Label>
          <Input maxLength={80} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Diwali, family function" data-testid="holiday-note-input" />
        </div>
        <Button onClick={add} disabled={busy} data-testid="holiday-add-btn" className="font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Plus className="h-4 w-4 mr-1.5" /> Add closure</>)}
        </Button>
      </div>

      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : upcoming.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="holiday-empty">No upcoming holiday closures. Weekly hours apply every day.</p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border" data-testid="holiday-list">
          {upcoming.map((h) => (
            <li key={h.id} className="flex items-center gap-3 px-3.5 py-2.5" data-testid={`holiday-row-${h.date}`}>
              <CalendarOff className="h-4 w-4 text-amber-600 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm">{pretty(h.date)}{h.date === today && <span className="ml-2 text-xs font-semibold text-amber-700">TODAY</span>}</p>
                {h.note && <p className="text-xs text-muted-foreground truncate">{h.note}</p>}
              </div>
              <Button variant="ghost" size="sm" onClick={() => del(h)} data-testid={`holiday-delete-${h.date}`} className="text-destructive hover:text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
